import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiOpenInNew } from '@mdi/js';
import { BacklinkLocationState } from '../PageTitle/PageTitle';
import { byReportType } from 'relevant-shared/reportData/reportType';

export function isInternalUrl(url: string) {
	return !url.startsWith('http') || url.startsWith(window.location.origin);
}

export function parseReportUrl(url: string) {
	try {
		const parsed = new URL(url);
		const match = parsed.pathname.match(/^(\/(?:generic-reports|reports)\/\w+)\/?(\w+)/);
		if (!match) {
			return null;
		}
		const [type, def] = Object.entries(byReportType).find(([type, value]) => value.reportLocation === match[1]);
		return { type, def, new: match[2] === 'add' };
	} catch (e) {
		console.error(`Invalid report URL: ${url}`, e);
		return null;
	}
}

export type LinkProps = {
	href?: string;
	hrefState?: Record<string, unknown> & BacklinkLocationState;
	onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
	unstyled?: boolean;
	className?: string;
	isDownloadLink?: boolean;
	children: React.ReactNode;
	/** @deprecated Use href instead */
	to?: string | { pathname: string; state?: BacklinkLocationState };
};

type RefType = HTMLAnchorElement | HTMLButtonElement;

export const Link = forwardRef<RefType, LinkProps>(({ to, href: hrefProp, hrefState, unstyled, className, onClick, isDownloadLink, children, ...rest }, ref) => {
	const history = useHistory();

	// Handle backwards compatibility for 'to' prop
	const href = hrefProp ?? (typeof to === 'string' ? to : to?.pathname);

	const commonProps = {
		className: clsx(
			!unstyled && 'text-sm font-semibold text-component-link-text-default hover:underline hover:text-component-link-text-hover focus:underline focus:text-component-link-text-hover active:underline active:text-component-link-text-pressed cursor-pointer',
			onClick && !href && 'no-underline!', // Hide underline for buttons masquerading as links (legacy)
			(href === children?.toString()) && 'break-all', // Break long URLs anywhere
			className,
		),
		onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
			onClick?.(e);
			if (e.defaultPrevented) {
				return;
			}
			if (href && isInternalUrl(href) && !isDownloadLink) {
				e.preventDefault();
				history.push(href, hrefState);
			}
		},
	};

	if (href) {
		return (
			// eslint-disable-next-line react/jsx-no-target-blank
			<a
				ref={ref as React.Ref<HTMLAnchorElement>}
				href={href}
				target={isInternalUrl(href) ? '_self' : '_blank'}
				rel={isInternalUrl(href) ? undefined : 'noopener noreferrer'}
				{...commonProps}
				{...rest}
			>
				{children}
				{!isInternalUrl(href) && !unstyled && <Icon path={mdiOpenInNew} className="ml-1 -mt-1 size-[18px] inline-block" />}
			</a>
		);
	} else {
		return <button type="button" {...commonProps} {...rest} ref={ref as React.Ref<HTMLButtonElement>}>{children}</button>;
	}
});
